import React, { useState, useMemo } from 'react';
import { X, Clock, Calendar } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

/**
 * ScheduleSendModal — small picker overlaid on top of ComposeModal.
 *
 * Lets the user pick when to send a draft. Presets cover the common cases
 * (in 1 hour, tomorrow 9 AM, Monday 9 AM); custom date/time is also
 * available. Resolves to a UTC ISO datetime string.
 */
function nextOccurrenceAt(dayOffset, hour, minute = 0) {
  const d = new Date();
  d.setDate(d.getDate() + dayOffset);
  d.setHours(hour, minute, 0, 0);
  return d;
}

function nextMonday9am() {
  const d = new Date();
  d.setHours(9, 0, 0, 0);
  // 1 = Monday
  const dayOfWeek = d.getDay();
  const daysUntilMon = ((1 - dayOfWeek + 7) % 7) || 7;
  d.setDate(d.getDate() + daysUntilMon);
  return d;
}

function formatHumanLocal(date) {
  if (!date || isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function toInputValue(date) {
  // YYYY-MM-DDTHH:MM in local time for <input type="datetime-local" />
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export default function ScheduleSendModal({ open, onClose, onConfirm }) {
  const presets = useMemo(() => {
    const now = new Date();
    return [
      { id: '1h', label: 'In 1 hour', date: new Date(now.getTime() + 60 * 60 * 1000) },
      { id: '4h', label: 'In 4 hours', date: new Date(now.getTime() + 4 * 60 * 60 * 1000) },
      { id: 'tomorrow9', label: 'Tomorrow at 9:00 AM', date: nextOccurrenceAt(1, 9) },
      { id: 'monday9', label: 'Monday at 9:00 AM', date: nextMonday9am() },
    ];
  }, [open]);

  const [customValue, setCustomValue] = useState(toInputValue(nextOccurrenceAt(1, 9)));

  if (!open) return null;

  const handlePreset = (date) => {
    onConfirm?.(date.toISOString(), formatHumanLocal(date));
  };

  const handleCustom = () => {
    if (!customValue) return;
    const dt = new Date(customValue);
    if (isNaN(dt.getTime())) return;
    if (dt.getTime() <= Date.now() + 30000) {
      // Friendly inline guard — backend re-validates
      alert('Pick a time at least 30 seconds in the future.');
      return;
    }
    onConfirm?.(dt.toISOString(), formatHumanLocal(dt));
  };

  return (
    <div className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-4" data-testid="schedule-send-modal">
      <div className="bg-white w-full max-w-md rounded-xl shadow-2xl overflow-hidden">
        <header className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
          <h2 className="text-base font-semibold flex items-center gap-2">
            <Clock size={16} className="text-primary" />
            Send later
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0" data-testid="schedule-close-btn">
            <X size={18} />
          </Button>
        </header>

        <div className="p-4 space-y-2">
          {presets.map(p => (
            <button
              key={p.id}
              onClick={() => handlePreset(p.date)}
              data-testid={`schedule-preset-${p.id}`}
              className="w-full text-left flex items-center justify-between px-3 py-2 rounded-lg border hover:bg-muted/50 transition"
            >
              <span className="text-sm font-medium">{p.label}</span>
              <span className="text-xs text-muted-foreground">{formatHumanLocal(p.date)}</span>
            </button>
          ))}

          <div className="pt-3 mt-2 border-t">
            <label className="text-xs font-medium flex items-center gap-1.5 mb-1.5 text-muted-foreground">
              <Calendar size={14} />
              Or pick a specific time
            </label>
            <div className="flex gap-2">
              <Input
                type="datetime-local"
                value={customValue}
                onChange={e => setCustomValue(e.target.value)}
                data-testid="schedule-custom-input"
                className="flex-1"
              />
              <Button onClick={handleCustom} data-testid="schedule-custom-confirm" size="sm">
                Schedule
              </Button>
            </div>
          </div>
        </div>

        <footer className="px-4 py-3 border-t bg-muted/30 flex justify-end">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
        </footer>
      </div>
    </div>
  );
}
